import { conformsTo, isBoolean, isNullable, isNumber, isString } from '@dmb/core';

import { ContributorAssignment } from './contributors';
import { UploadInfo } from './general';
import { TrackMetadata } from './track-metadata';

export type NewTrack = {
  id: string;
  existing?: false;
  title: string;
  downloadReleaseDate?: Date;
  streamingReleaseDate?: Date;
  titleVersion: string;
  isrc: string;
  isrcWasGenerated: boolean;
  file: File | undefined;
  contributors: ContributorAssignment[];
  metadata: TrackMetadata;
  uploadInfo: UploadInfo;
  formatInfos?: {
    duration: number | undefined;
    container_format: 'wav';
    channels: number;
    sample_rate: number;
    bits_per_sample: 8 | 16 | 24 | 32 | undefined;
    bitrate: number;
  };
};

export type ExistingTrack = {
  id: number;
  existing: true;
  title: string;
  titleVersion: string;
  isrc: string;
  duration?: number;
  downloadReleaseDate?: Date;
  streamingReleaseDate?: Date;
  displayArtist?: string;
};

export type Track = NewTrack | ExistingTrack;

type TrackType = 'AUDIO' | 'VIDEO';
type TrackSubtype = 'MUSIC' | 'AUDIOBOOK' | 'RADIO_PLAY' | 'RINGTONE' | 'STEM';
type RevisionStatus = 'CREATED' | 'IMPORTED' | 'EDITING' | 'IN-QC' | 'PREPARED' | 'CONFIRMED';
type AudioQuality = 'SD' | 'HD';

function isTrackType(val: unknown): val is TrackType {
  return val === 'AUDIO' || val === 'VIDEO';
}
function isTrackSubtype(val: unknown): val is TrackSubtype {
  return val === 'MUSIC' || val === 'AUDIOBOOK' || val === 'RADIO_PLAY' || val === 'RINGTONE' || val === 'STEM';
}
function isRevisionStatus(val: unknown): val is RevisionStatus {
  return (
    val === 'CREATED' ||
    val === 'IMPORTED' ||
    val === 'EDITING' ||
    val === 'IN-QC' ||
    val === 'PREPARED' ||
    val === 'CONFIRMED'
  );
}
function isAudioQuality(val: unknown): val is AudioQuality {
  return val === 'SD' || val === 'HD';
}

export type TrackSearchItem = {
  track_id: number;
  track_type?: null | TrackType;
  track_subtype?: null | TrackSubtype;
  revision_status: RevisionStatus;
  isrc?: null | string;
  audio_quality?: null | AudioQuality;
  is_apple_digital_master: boolean;
  is_immersive_audio_available: boolean;
  title?: null | string;
  title_version?: null | string;
  display_artist?: null | string;
  duration?: null | number;
};

export const isTrackSearchItem = conformsTo<TrackSearchItem>({
  track_id: isNumber,
  track_type: isNullable(isTrackType),
  track_subtype: isNullable(isTrackSubtype),
  revision_status: isRevisionStatus,
  isrc: isNullable(isString),
  audio_quality: isNullable(isAudioQuality),
  is_apple_digital_master: isBoolean,
  is_immersive_audio_available: isBoolean,
  title: isNullable(isString),
  title_version: isNullable(isString),
  display_artist: isNullable(isString),
  duration: isNullable(isNumber),
});
